import { CSSObject } from '@emotion/react'
import React from 'react'
import { AssetReference } from '@opallabs/cassini'
import { kuiperClassNames, renderIcon } from '@opallabs/kuiper'
import { AccountName } from '@opallabs/periodic/src/AccountName'
import { ContentAvatar } from '@opallabs/periodic/src/ContentAvatar'
import { ContentTextInput } from '@opallabs/periodic/src/ContentTextInput'
import {
  sharedStyles,
  sharedColors,
} from '../../../sharedDisplays/sharedStyles'
import {
  styles,
  getCaptionStyles,
  getOverlayStyles,
  getMusicInputStyles,
} from './styles'

export type OverlayProps = {
  accountImage?: AssetReference
  accountName: string
  accountUsername: string
  collabAccount?: string
  content: string
  contentCharacterLimit: number
  contentPlaceholderText: string
  effect: string
  instagramCollab?: boolean
  footerRef: React.LegacyRef<HTMLDivElement> | null
  onContentChange: (value: string) => void
  onMusicChange: (value: string) => void
  setIsHovered: (state: boolean) => void
  truncate: boolean
  readOnly: boolean
  musicTitle: string
}

type AccountNameProps = {
  name: string
  color: string
  style: CSSObject
}

const iconStyles = {
  height: 24,
  width: 24,
  ariaMode: 'hidden' as const,
}

const getSecondAccountProps = (collabAccount?: string): AccountNameProps => ({
  name: collabAccount || 'No collab account',
  color: collabAccount ? 'white' : (sharedColors.emptyCollab as string),
  style: styles.accountNameStyles,
})

export const Overlay: React.FC<OverlayProps> = ({
  accountImage = null,
  accountUsername,
  collabAccount,
  content,
  contentCharacterLimit,
  contentPlaceholderText,
  effect,
  footerRef,
  instagramCollab,
  readOnly,
  onContentChange,
  onMusicChange,
  musicTitle,
  setIsHovered,
  truncate,
}) => {
  const [captionValue, setCaptionValue] = React.useState(content || '')
  const [musicValue, setMusicValue] = React.useState(musicTitle || '')

  const handleBlur = (type: string): void => {
    if (type === 'caption') {
      onContentChange(captionValue)
    } else {
      onMusicChange(musicValue)
    }
  }

  return (
    <div
      css={getOverlayStyles(truncate)}
      className={kuiperClassNames('mobileSingle-overlay')}
    >
      <div
        css={styles.overlayHeaderStyles}
        className={kuiperClassNames('mobileSingle-overlay-header')}
      >
        <span css={styles.titleStyles}>Reels</span>
        <span>
          {renderIcon('instagram-mobile-camera-inverted', iconStyles)}
        </span>
      </div>
      <div
        className={kuiperClassNames('mobileSingle-overlay-stats-and-messages')}
        css={styles.statsAndMessagesStyles}
      >
        <span>
          {renderIcon('instagram-mobile-heart', { ...iconStyles, height: 22 })}
        </span>
        <span aria-hidden css={styles.numberTextStyles}>
          ###
        </span>
        <span>{renderIcon('instagram-mobile-comment', iconStyles)}</span>
        <span aria-hidden css={styles.numberTextStyles}>
          ###
        </span>
        <span>
          {renderIcon('instagram-mobile-send', {
            ...iconStyles,
            height: 22,
            width: 25,
          })}
        </span>
        <span css={{ marginRight: '3px' }}>
          {renderIcon('instagram-mobile-more', { ariaMode: 'hidden' })}
        </span>
      </div>
      <div
        className={kuiperClassNames('mobileSingle-overlay-footer')}
        css={styles.getFooterStyles(readOnly)}
        ref={footerRef}
        onMouseEnter={() => setIsHovered(false)}
      >
        {effect && (
          <div
            className={kuiperClassNames('mobileSingle-effect')}
            css={styles.effectContainerStyles}
          >
            <div
              className={kuiperClassNames('effect-text')}
              css={styles.effectTextStyles}
            >
              {renderIcon('instagram-mobile-effect', {
                ...iconStyles,
                height: 12,
                width: 12,
              })}
              <span>{effect}</span>
            </div>
          </div>
        )}
        <div
          className={kuiperClassNames('mobileSingle-overlay-account')}
          css={styles.accountStyles}
        >
          {instagramCollab ? (
            <React.Fragment>
              <div css={sharedStyles.collabAvatarStyles as CSSObject}>
                <ContentAvatar assetReference={accountImage} size={24} />
                <ContentAvatar
                  assetReference={null}
                  size={24}
                  style={sharedStyles.secondAvatarStyles as CSSObject}
                />
              </div>
              <div css={styles.accountNameWrapperStyles}>
                <AccountName
                  name={accountUsername}
                  color={'white'}
                  style={styles.accountNameStyles}
                />
                <span> and </span>
                <AccountName {...getSecondAccountProps(collabAccount)} />
              </div>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <div>
                <ContentAvatar assetReference={accountImage} />
              </div>
              <div css={styles.accountNameWrapperStyles}>
                <AccountName
                  name={accountUsername}
                  color={'white'}
                  style={styles.accountNameStyles}
                />
              </div>
              <div css={styles.followButtonStyles}>
                <div>Follow</div>
              </div>
            </React.Fragment>
          )}
        </div>
        <div
          className={kuiperClassNames('mobileSingle-overlay-caption')}
          css={styles.captionContainerStyles}
        >
          <div onBlur={() => handleBlur('caption')}>
            <ContentTextInput
              readOnly={readOnly || truncate}
              highlightSocialTags={false}
              value={truncate ? captionValue.split('\n')[0] : captionValue}
              placeholder={contentPlaceholderText}
              placeholderStyle={styles.captionPlaceholderStyles}
              truncate={truncate}
              truncationLimit={Math.min(
                captionValue?.split('\n')[0]?.length ?? 0,
                125
              )}
              maxRows={12}
              style={getCaptionStyles(truncate, readOnly)}
              characterLimit={contentCharacterLimit}
              characterCounterOptions={{ zIndex: 9999 }}
              onChange={(val: string): void => setCaptionValue(val)}
            />
          </div>
        </div>
        <div
          className={kuiperClassNames('mobileSingle-overlay-music')}
          css={styles.musicContainerStyles}
        >
          <div css={styles.musicIconPadding}>
            {renderIcon('instagram-mobile-sound', {
              ariaMode: 'hidden',
              height: 12,
              width: 12,
            })}
          </div>
          <div
            onBlur={() => handleBlur('music')}
            css={getMusicInputStyles(truncate, readOnly)}
          >
            <input
              aria-label="Content Music Text"
              readOnly={truncate || readOnly}
              value={musicValue}
              placeholder={'Add music...'}
              onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                setMusicValue(e.target.value)
              }
            />
          </div>
          <div css={styles.musicArtStyles}>
            <ContentAvatar
              borderRadius={'5px'}
              size={24}
              assetReference={accountImage}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
